import React, { useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Hash, Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";

export default function Numerology() {
  const [form, setForm] = useState({ name: "", dob: "" });
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const calc = async () => {
    if (!form.name || !form.dob) return toast.error("Name and DOB required");
    setBusy(true);
    try {
      const { data } = await api.post("/numerology/calculate", form);
      setResult(data);
    } catch { toast.error("Failed"); }
    setBusy(false);
  };

  return (
    <div className="space-y-6" data-testid="numerology-page">
      <div>
        <div className="text-[10px] uppercase tracking-[0.3em] text-amber-600 dark:text-amber-400">Sacred Numbers</div>
        <h1 className="font-display text-3xl md:text-4xl font-light tracking-tight mt-1">Numerology Calculator</h1>
        <p className="text-sm text-slate-500 mt-1">Mulank · Bhagyank · Lo Shu (Loso) Grid · Life-path predictions</p>
      </div>

      <Card className="p-6 rounded-2xl border-slate-200 dark:border-slate-800 max-w-2xl">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div><Label className="text-xs uppercase tracking-widest text-slate-500">Full name</Label><Input value={form.name} onChange={e=>setForm({...form,name:e.target.value})} className="mt-1 h-11 rounded-xl" data-testid="num-name" /></div>
          <div><Label className="text-xs uppercase tracking-widest text-slate-500">Date of birth</Label><Input type="date" value={form.dob} onChange={e=>setForm({...form,dob:e.target.value})} className="mt-1 h-11 rounded-xl" data-testid="num-dob" /></div>
        </div>
        <Button onClick={calc} className="mt-4 bg-[#1E3A8A] btn-pill" disabled={busy} data-testid="num-calc-btn">
          {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Hash className="w-4 h-4 mr-1" />} Calculate
        </Button>
      </Card>

      {result && (
        <div className="space-y-4">
          {/* Core numbers */}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
            {[
              ["Mulank", result.mulank, "Birth day root"],
              ["Bhagyank", result.bhagyank, "Destiny sum"],
              ["Life Path", result.life_path_number, "Journey"],
              ["Destiny", result.destiny_number, "Consonants"],
              ["Expression", result.expression_number, "Full name"],
              ["Soul", result.soul_number, "Vowels"],
              ["Lucky", result.lucky_number, "Day"],
            ].map(([lbl, val, sub]) => (
              <Card key={lbl} className="p-4 rounded-2xl text-center border-slate-200 dark:border-slate-800 bg-gradient-to-br from-white to-amber-50/30 dark:from-slate-900/60 dark:to-amber-500/5" data-testid={`num-${lbl.toLowerCase().replace(' ','-')}`}>
                <div className="text-[9px] uppercase tracking-[0.22em] text-slate-500">{lbl}</div>
                <div className="font-display text-4xl font-light text-amber-600 dark:text-amber-400 mt-1">{val}</div>
                <div className="text-[9px] text-slate-500 mt-0.5">{sub}</div>
              </Card>
            ))}
          </div>

          {/* Mulank + Bhagyank predictions */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {result.mulank_prediction && (
              <Card className="p-5 rounded-2xl border-slate-200 dark:border-slate-800" data-testid="mulank-prediction">
                <div className="flex items-center gap-2 mb-2">
                  <Sparkles className="w-4 h-4 text-amber-500" />
                  <h3 className="font-display text-lg">Mulank {result.mulank} — Personality</h3>
                </div>
                <p className="text-sm text-slate-700 dark:text-slate-200">{result.mulank_prediction}</p>
              </Card>
            )}
            {result.bhagyank_prediction && (
              <Card className="p-5 rounded-2xl border-slate-200 dark:border-slate-800" data-testid="bhagyank-prediction">
                <div className="flex items-center gap-2 mb-2">
                  <Sparkles className="w-4 h-4 text-amber-500" />
                  <h3 className="font-display text-lg">Bhagyank {result.bhagyank} — Destiny</h3>
                </div>
                <p className="text-sm text-slate-700 dark:text-slate-200">{result.bhagyank_prediction}</p>
              </Card>
            )}
          </div>

          {/* Lo Shu Grid */}
          {result.lo_shu && (
            <Card className="p-6 rounded-2xl border-slate-200 dark:border-slate-800" data-testid="lo-shu-grid">
              <div className="flex items-center gap-2 mb-3">
                <Hash className="w-5 h-5 text-amber-500" />
                <h3 className="font-display text-xl">Lo Shu (Loso) Grid</h3>
              </div>
              <div className="flex flex-col md:flex-row gap-6 items-start">
                <div className="mx-auto md:mx-0">
                  <div className="grid grid-cols-3 gap-1 w-56">
                    {[
                      [4, result.lo_shu.grid[0][0]], [9, result.lo_shu.grid[0][1]], [2, result.lo_shu.grid[0][2]],
                      [3, result.lo_shu.grid[1][0]], [5, result.lo_shu.grid[1][1]], [7, result.lo_shu.grid[1][2]],
                      [8, result.lo_shu.grid[2][0]], [1, result.lo_shu.grid[2][1]], [6, result.lo_shu.grid[2][2]],
                    ].map(([n, count]) => (
                      <div key={`loshu-${n}`} className={`aspect-square rounded-xl border-2 flex flex-col items-center justify-center text-center ${
                        count === 0 ? "border-red-200 bg-red-50/40 dark:bg-red-500/5 dark:border-red-500/30" :
                        count >= 2  ? "border-emerald-300 bg-emerald-50 dark:bg-emerald-500/10 dark:border-emerald-500/40" :
                                       "border-amber-300 bg-amber-50 dark:bg-amber-500/10 dark:border-amber-500/40"
                      }`}>
                        <div className="text-[9px] uppercase text-slate-500 leading-none">Cell</div>
                        <div className="font-display text-2xl font-semibold leading-none mt-0.5">
                          {count > 0 ? String(n).repeat(count) : "—"}
                        </div>
                        <div className="text-[9px] text-slate-500 leading-none mt-0.5">{n}·{count}</div>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="flex-1 space-y-3">
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">Strong numbers</div>
                    <div className="flex flex-wrap gap-1.5">
                      {result.lo_shu.strong.length > 0
                        ? result.lo_shu.strong.map(n => <Badge key={n} className="bg-emerald-100 text-emerald-800">{n}</Badge>)
                        : <span className="text-xs text-slate-500">None</span>}
                    </div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">Missing numbers</div>
                    <div className="flex flex-wrap gap-1.5">
                      {result.lo_shu.missing.length > 0
                        ? result.lo_shu.missing.map(n => <Badge key={n} className="bg-red-100 text-red-800">{n}</Badge>)
                        : <span className="text-xs text-slate-500">None — balanced grid</span>}
                    </div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">Guidance</div>
                    <p className="text-sm text-slate-700 dark:text-slate-200">{result.lo_shu.missing_meaning}</p>
                  </div>
                </div>
              </div>
            </Card>
          )}

          <Card className="p-6 rounded-2xl bg-white dark:bg-slate-900/60">
            <div className="text-[10px] uppercase tracking-widest text-slate-500">Life-path Guidance</div>
            <div className="mt-2 text-lg text-slate-700 dark:text-slate-200 italic">"{result.recommendations}"</div>
          </Card>
        </div>
      )}
    </div>
  );
}
